import { clearToken, getToken } from "../auth/token.js";
import { env } from "../env.js";

// The single error envelope: every non-2xx response is { error: {...} }.
export interface ErrorEnvelope {
  error: {
    code: string;
    message: string;
    details?: unknown;
    requestId?: string;
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  init?: RequestInit,
): Promise<T> {
  const url = path.startsWith("http") ? path : `${env.apiBase}${path}`;
  const token = getToken();
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...((init?.headers as Record<string, string> | undefined) ?? {}),
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: "omit", // cookie-less: auth rides in the Authorization header
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...init,
    });
  } catch (e) {
    throw new ApiError(0, "NETWORK", (e as Error).message || "Network error");
  }

  if (res.status === 401) clearToken(); // stale/invalid token -> drop it

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const envelope = parsed as Partial<ErrorEnvelope> | undefined;
    const err = envelope?.error;
    throw new ApiError(
      res.status,
      err?.code ?? `HTTP_${res.status}`,
      err?.message ?? res.statusText ?? "Request failed",
      err?.details,
    );
  }

  return parsed as T;
}

export const api = {
  get: <T>(path: string, init?: RequestInit) =>
    request<T>("GET", path, undefined, init),
  post: <T>(path: string, body?: unknown, init?: RequestInit) =>
    request<T>("POST", path, body, init),
  patch: <T>(path: string, body?: unknown, init?: RequestInit) =>
    request<T>("PATCH", path, body, init),
  del: <T>(path: string, init?: RequestInit) =>
    request<T>("DELETE", path, undefined, init),
};
